import { clsx } from 'clsx';
import type { LucideIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

// Filled buttons darken on hover/press. 700+ shades turn light in the dark theme, so there it fades the fill over the dark page instead.
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-ember-500 text-white shadow-xs hover:bg-ember-600 active:bg-ember-700 dark:active:bg-ember-600/80',
  secondary:
    'border border-slate-300 bg-surface text-slate-700 shadow-xs hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 active:bg-slate-100',
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200/70',
  danger: 'bg-red-600 text-white shadow-xs hover:bg-red-700 active:bg-red-800 dark:hover:bg-red-600/85 dark:active:bg-red-600/70',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 gap-1 px-2.5 text-xs',
  md: 'h-8 gap-1.5 px-3 text-[13px]',
};

const ICON_SIZES: Record<ButtonSize, number> = { sm: 13, md: 15 };

const BASE =
  'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500 disabled:pointer-events-none disabled:opacity-50';

export type ButtonProps = Omit<ComponentProps<'button'>, 'children'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon; replaced by a spinner while `loading`. */
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  /** Disables the button and shows a spinner. */
  loading?: boolean;
  children?: ReactNode;
};

/** Standard button. Defaults to `type="button"` so it never submits a form by accident. */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  loading = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const iconSize = ICON_SIZES[size];
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={clsx(BASE, VARIANTS[variant], SIZES[size], className)}
      {...rest}
    >
      {loading ? <Spinner size={iconSize} /> : Icon ? <Icon size={iconSize} aria-hidden className="shrink-0" /> : null}
      {children}
      {IconRight && <IconRight size={iconSize} aria-hidden className="shrink-0" />}
    </button>
  );
}

export type IconButtonProps = Omit<ComponentProps<'button'>, 'children'> & {
  icon: LucideIcon;
  /** Accessible name; also shown as the native tooltip. */
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

/** Square icon-only button with a required accessible label. */
export function IconButton({
  icon: Icon,
  label,
  variant = 'ghost',
  size = 'md',
  loading = false,
  disabled,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const iconSize = ICON_SIZES[size] + 1;
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      className={clsx(BASE, VARIANTS[variant], size === 'sm' ? 'size-7' : 'size-8', className)}
      {...rest}
    >
      {loading ? <Spinner size={iconSize} /> : <Icon size={iconSize} aria-hidden />}
    </button>
  );
}
